import {
  Ref,
  useRef,
  useState,
  useImperativeHandle,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  ItemNoAndType,
  MediaOrderedDetails,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { isLoading } from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "../InvalidCreatorFragment/InvalidCreatorFragment";
import { useMediasAndArticleCount } from "@coworker/app/src/components/RoomSettings/hooks/useMediasAndArticleCount";
import TitledTextWithModal from "../../../TitledTextWithModal/TitledTextWithModal";
import ListItemWithControls from "@coworker/apprestructured/src/task-management/components/ListItemWithControls/ListItemWithControls";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaQuantityStepper } from "@coworker/apprestructured/src/shared/wrappers/FixaQuantityStepper/FixaQuantityStepper";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import FixaCheckbox from "@coworker/apprestructured/src/shared/wrappers/FixaCheckbox/FixaCheckbox";
import { useTranslation } from "@coworker/locales";
import {
  groupMediasByType,
  getUniqueMediaCount,
  calculateTotalQuantity,
  getMediaItemKey,
  getMediaItemTitle,
  getMediaItemDescription,
  findMediaDetail,
} from "@coworker/apprestructured/src/task-management/helpers/media.helper";
import {
  getMediaTypeText,
  getAreaDisplayName,
} from "@coworker/app/src/components/RoomSettings/tools";
import { MediaTypes } from "@coworker/app/src/components/RoomSettings/constants";
import styles from "./MediasPrintPtagsCreatorFragment.module.css";
import { useStoreId } from "@coworker/app/src/core/auth/useLoggedInUser";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { useMediaResetOnReasonChange } from "@coworker/apprestructured/src/task-management/hooks/useMediaResetOnReasonChange";

export interface MediasPrintPtagsCreatorFragmentConfigProps {
  title: string;
  field: string;
  visibility: VisibilityCondition;
}

interface MediasPrintPtagsCreatorFragmentProps {
  configProps: MediasPrintPtagsCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
}

const MediasPrintPtagsCreatorFragment = ({
  configProps,
  commonProps,
  ref,
}: MediasPrintPtagsCreatorFragmentProps) => {
  const { field, visibility } = configProps;
  const { task, taskType, onTaskChange, showValidation } = commonProps;
  const storeId = useStoreId();

  const mediasWithPieces = validateInitialDetailsFieldValue<
    MediaOrderedDetails[]
  >(getByPath(task, field), field, taskType);

  const itemNoAndType = validateInitialDetailsFieldValue<ItemNoAndType>(
    getByPath(task, "details.item"),
    "details.item",
    taskType
  );

  // Reset media values when reason changes
  useMediaResetOnReasonChange(
    isLoading(task) ? {} : (task as Record<string, unknown>),
    onTaskChange
  );

  // Check if mandatory field is filled - medias must exist
  // P-tags values can be 0 (via checkbox), but medias array must exist and have items
  // During loading state, consider it valid to avoid showing errors prematurely
  const valid =
    !isLoading(mediasWithPieces) &&
    Array.isArray(mediasWithPieces) &&
    mediasWithPieces.length > 0;

  const isVisible =
    !isLoading(task) &&
    shouldShowField(task as Record<string, any>, visibility);

  const fragmentValid = !isVisible || valid;

  // Expose validation state to parent via ref
  useImperativeHandle(ref, () => {
    return { valid: fragmentValid };
  }, [fragmentValid]);

  // Track whether initialization has been done to prevent re-initialization after save
  const initializedRef = useRef(false);
  const taskIdRef = useRef<string | null>(null);

  // Sparse overrides for ptags_ordered edited in this modal session, keyed by
  // "mediaId:articleId". Falls back to the live task value otherwise, so
  // external resets (e.g. reason change) are never masked by stale state.
  const [ptagsQuantityOverrides, setPtagsQuantityOverrides] = useState<
    Record<string, number>
  >({});

  // Reset initialization flag when task changes (e.g., when user switches to a different task)
  useEffect(() => {
    const currentTaskId = isLoading(task)
      ? null
      : (getByPath(task, "id") as string | null);

    if (currentTaskId !== taskIdRef.current) {
      initializedRef.current = false;
      taskIdRef.current = currentTaskId;
      setPtagsQuantityOverrides({});
    }
  }, [task]);

  // Initialize defaults when medias arrive without ptags_ordered or pieces_ordered
  useEffect(() => {
    // Skip if already initialized to prevent race condition after save
    if (initializedRef.current) return;

    if (
      Array.isArray(mediasWithPieces) &&
      !isLoading(mediasWithPieces) &&
      mediasWithPieces.length > 0 &&
      mediasWithPieces.some(
        (media) => !("ptags_ordered" in media) || !("pieces_ordered" in media)
      )
    ) {
      const initialized = mediasWithPieces.map((media) => ({
        ...media,
        pieces_ordered: media.pieces_ordered ?? {
          quantity: media.pieces,
          completed: false,
        },
        ptags_ordered: media.ptags_ordered ?? {
          quantity: 1,
          completed: false,
        },
      }));
      onTaskChange({ details: { medias: initialized } });
      initializedRef.current = true;
    }
  }, [mediasWithPieces, onTaskChange]);

  // Derived every render from the task so reason-driven resets (or any other
  // external change to ptags_ordered) are always reflected. Only media the
  // user has explicitly edited in this modal session get overridden — see
  // ArticlesQuantityCreatorFragment for the same pattern.
  const mediasOrderedDetails: MediaOrderedDetails[] | null = Array.isArray(
    mediasWithPieces
  )
    ? mediasWithPieces.map((media) => {
        const key = `${media.id}:${media.article_id}`;
        const baseptagsOrdered = media.ptags_ordered ?? {
          quantity: 1,
          completed: false,
        };
        return {
          ...media,
          pieces_ordered: media.pieces_ordered ?? {
            quantity: media.pieces,
            completed: false,
          },
          ptags_ordered:
            key in ptagsQuantityOverrides
              ? {
                  quantity: ptagsQuantityOverrides[key]!,
                  completed: false,
                }
              : baseptagsOrdered,
        };
      })
    : null;

  const handlePTagsChange = useCallback(
    (mediaId: string, articleId: string, newPTags: number) => {
      setPtagsQuantityOverrides((prev) => ({
        ...prev,
        [`${mediaId}:${articleId}`]: newPTags,
      }));
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (Array.isArray(mediasOrderedDetails)) {
      onTaskChange({ details: { medias: mediasOrderedDetails } });
      setPtagsQuantityOverrides({});
    }
  }, [mediasOrderedDetails, onTaskChange]);

  const { data: mediasAndArticleCount, isLoading: isFetchingMedias } =
    useMediasAndArticleCount(
      isLoading(itemNoAndType) ? "" : itemNoAndType?.no,
      storeId
    );

  const { t } = useTranslation();

  const handleCheckboxChange = useCallback(
    (mediaId: string, articleId: string, checked: boolean) => {
      if (checked) {
        handlePTagsChange(mediaId, articleId, 0);
      } else {
        handlePTagsChange(mediaId, articleId, 1);
      }
    },
    [handlePTagsChange]
  );

  // Filter medias to get matching ones from API response
  const matchedMedias =
    mediasAndArticleCount?.filter(
      (media) =>
        Array.isArray(mediasWithPieces) &&
        mediasWithPieces.some(
          (m) => m.id === media.mediaId && m.article_id === media.articleId
        )
    ) ?? [];

  // Group medias
  const groupedMedias = useMemo(
    () => groupMediasByType(matchedMedias),
    [matchedMedias]
  );

  // Calculate description
  const description = useMemo(() => {
    if (isLoading(mediasOrderedDetails)) return "";
    const totalPTags = calculateTotalQuantity(
      mediasOrderedDetails,
      "ptags_ordered"
    );
    const uniqueMediaCount = getUniqueMediaCount(mediasOrderedDetails);
    const ptagsText = t("ptagsCountString", { count: totalPTags });
    const mediaText = t("mediaCountString", { count: uniqueMediaCount });
    return `${ptagsText} ${t("inString").toLowerCase()} ${mediaText}`;
  }, [mediasOrderedDetails, t]);

  // Build modal content
  const modalContent = useMemo(() => {
    if (isLoading(mediasOrderedDetails)) return null;

    return (
      <>
        <FixaInlineMessage
          variant="informative"
          title={
            t("setPtagsQuantityForMediaString") ||
            "Set the needed number of P-tags for the different media"
          }
          body={
            t("quantityCannotExceedExistingAmountString") ||
            "The quantity cannot exceed the existing amount in each media."
          }
        />
        {Object.entries(groupedMedias).map(([mediaType, medias]) => (
          <FixaListView key={mediaType} id={`media-type-${mediaType}`}>
            <FixaListViewItem
              size="small"
              description={getMediaTypeText(mediaType as MediaTypes)}
              key={`${mediaType}-header`}
            />

            {medias.map((media) => {
              const detail = findMediaDetail(media, mediasOrderedDetails);
              const pieces = detail?.pieces ?? media.nbrArticles;
              const ptagsOrdered = detail?.ptags_ordered?.quantity ?? 1;
              const isChecked = ptagsOrdered === 0;
              const compositeKey = getMediaItemKey(media);
              const areaDisplayName = getAreaDisplayName(media.area, t);

              return (
                <ListItemWithControls
                  key={compositeKey}
                  title={
                    <FixaText textStyle="Heading.S">
                      {getMediaItemTitle(media)}
                    </FixaText>
                  }
                  description={
                    <FixaText textStyle="Body.M">
                      {getMediaItemDescription(media)}
                    </FixaText>
                  }
                  quantityLabel={
                    <FixaText textStyle="Body.M">
                      {t("piecesCountString", { count: pieces })}
                    </FixaText>
                  }
                  addOn={areaDisplayName}
                >
                  <FixaQuantityStepper
                    small
                    fluid
                    value={isChecked ? 0 : ptagsOrdered}
                    ariaDescribedById={`media-ptags-quantity-stepper-${compositeKey}`}
                    ariaDescribedBy={`ptags-stepper-${compositeKey}`}
                    minVal={1}
                    maxVal={pieces}
                    disabled={isChecked}
                    onIncrease={() => {
                      const newValue = Math.min(ptagsOrdered + 1, pieces);
                      handlePTagsChange(
                        media.mediaId,
                        media.articleId,
                        newValue
                      );
                    }}
                    onDecrease={() => {
                      const newValue = Math.max(ptagsOrdered - 1, 1);
                      handlePTagsChange(
                        media.mediaId,
                        media.articleId,
                        newValue
                      );
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      const numValue = parseInt(inputValue, 10);
                      // Only update if we have a valid number
                      if (
                        !isNaN(numValue) &&
                        numValue >= 1 &&
                        numValue <= pieces
                      ) {
                        handlePTagsChange(
                          media.mediaId,
                          media.articleId,
                          numValue
                        );
                      }
                    }}
                  />
                  <div className={styles["checkboxContainer"]}>
                    <FixaText textStyle="Body.M">
                      {t("noPtagsNeededString") || "No P-tags needed"}
                    </FixaText>
                    <FixaCheckbox
                      id={`no-ptags-${compositeKey}`}
                      value={compositeKey}
                      checked={isChecked}
                      onChange={() =>
                        handleCheckboxChange(
                          media.mediaId,
                          media.articleId,
                          !isChecked
                        )
                      }
                      data-testid={`no-ptags-checkbox-${compositeKey}`}
                    />
                  </div>
                </ListItemWithControls>
              );
            })}
          </FixaListView>
        ))}
      </>
    );
  }, [
    groupedMedias,
    mediasOrderedDetails,
    t,
    handlePTagsChange,
    handleCheckboxChange,
  ]);

  if (!isVisible) {
    return null;
  }

  if (
    isFetchingMedias ||
    isLoading(mediasWithPieces) ||
    isLoading(itemNoAndType) ||
    isLoading(mediasOrderedDetails)
  ) {
    return <LoadingCreatorFragment />;
  }

  return (
    <TitledTextWithModal
      testId="medias-print-ptags-list-item"
      title={t("howManyPTagsString")}
      description={description}
      modalTitle={t("printPTagsString")}
      modalContent={modalContent}
      onConfirm={handleConfirm}
    />
  );
};

export default MediasPrintPtagsCreatorFragment;
